import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AUTOMATION_HUB_EVENT_TYPES,
  AUTOMATION_TRIGGER_TYPES,
  WEBHOOK_EVENT_LABELS,
  type AutomationHubEventType,
  type AutomationTriggerType,
} from '@marketing-hub/shared';
import { apiFetch, ApiError } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Field, TextArea, TextInput } from '@/components/ui/Field';
import { LoadingState } from '@/components/StateMessage/StateMessage';
import leadStyles from '@/features/leads/Leads.module.css';
import type { EditableAutomationStep } from './AutomationStepConfigEditor';
import { AutomationStepListEditor } from './AutomationStepListEditor';
import { TraceLink } from '@/features/traces/TraceLink';
import styles from './automations.module.css';

type Props = {
  projectId: string;
  automationId: string | null;
  isNew: boolean;
  onSaved: (id: string) => void;
  onRunSelect: (runId: string) => void;
};

export function AutomationEditor({ projectId, automationId, isNew, onSaved, onRunSelect }: Props) {
  const qc = useQueryClient();
  const [name, setName] = useState('Nieuwe automation');
  const [description, setDescription] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [triggerType, setTriggerType] = useState<AutomationTriggerType>('event');
  const [eventType, setEventType] = useState<AutomationHubEventType>('reply.received');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [steps, setSteps] = useState<EditableAutomationStep[]>([
    { step_order: 0, step_type: 'lead_add_tag', config: { tag: 'automation' }, enabled: true },
  ]);
  const [simulateOut, setSimulateOut] = useState<string | null>(null);

  const detailQ = useQuery({
    queryKey: ['automation-detail', automationId],
    enabled: Boolean(automationId) && !isNew,
    queryFn: () =>
      apiFetch<{
        automation: {
          name: string;
          description: string | null;
          enabled: boolean;
          trigger_type: AutomationTriggerType;
          trigger_config: { event_type?: string };
          filter: { all?: { path: string; op: string; value?: unknown }[] };
        };
        steps: EditableAutomationStep[];
      }>(`/api/automations/${automationId}`),
  });

  const agentsQ = useQuery({
    queryKey: ['agents', projectId],
    queryFn: () =>
      apiFetch<{ agents: { id: string; name: string; enabled: boolean }[] }>(
        `/api/agents?project_id=${projectId}`,
      ),
  });

  const runsQ = useQuery({
    queryKey: ['automation-runs', automationId],
    enabled: Boolean(automationId) && !isNew,
    queryFn: () =>
      apiFetch<{ runs: { id: string; status: string; created_at: string; trace_id: string | null }[] }>(
        `/api/automations/${automationId}/runs`,
      ),
  });

  useEffect(() => {
    if (!detailQ.data) return;
    const a = detailQ.data.automation;
    setName(a.name);
    setDescription(a.description ?? '');
    setEnabled(a.enabled);
    setTriggerType(a.trigger_type);
    if (a.trigger_config.event_type) {
      setEventType(a.trigger_config.event_type as AutomationHubEventType);
    }
    const camp = a.filter.all?.find((c) => c.path === 'payload.campaign_id' && c.op === 'eq');
    setCampaignFilter(typeof camp?.value === 'string' ? camp.value : '');
    setSteps(detailQ.data.steps);
  }, [detailQ.data]);

  function buildFilter() {
    if (campaignFilter.trim()) {
      return { all: [{ path: 'payload.campaign_id', op: 'eq' as const, value: campaignFilter.trim() }] };
    }
    return { all: [{ path: 'payload.lead_id', op: 'exists' as const }] };
  }

  function buildPayload() {
    return {
      project_id: projectId,
      name,
      description: description || undefined,
      enabled,
      trigger_type: triggerType,
      trigger_config:
        triggerType === 'event'
          ? { event_type: eventType }
          : triggerType === 'cron'
            ? { cron: '0 9 * * 1', timezone: 'Europe/Amsterdam' }
            : {},
      filter: buildFilter(),
      steps,
    };
  }

  const saveM = useMutation({
    mutationFn: async () => {
      const body = buildPayload();
      if (isNew || !automationId) {
        return apiFetch<{ automation: { id: string } }>('/api/automations', {
          method: 'POST',
          body: JSON.stringify(body),
        });
      }
      return apiFetch<{ automation: { id: string } }>(`/api/automations/${automationId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: body.name,
          description: body.description,
          enabled: body.enabled,
          trigger_config: body.trigger_config,
          filter: body.filter,
          steps: body.steps,
        }),
      });
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['automations', projectId] });
      onSaved(data.automation.id);
    },
  });

  const simulateM = useMutation({
    mutationFn: () =>
      apiFetch<{ simulation: { filter_pass: boolean; steps: { preview: string }[] } }>(
        '/api/automations/simulate',
        {
          method: 'POST',
          body: JSON.stringify({
            project_id: projectId,
            event_type: eventType,
            payload: campaignFilter.trim()
              ? { lead_id: '00000000-0000-4000-8000-000000000001', campaign_id: campaignFilter.trim() }
              : { lead_id: '00000000-0000-4000-8000-000000000001' },
            filter: buildFilter(),
            steps,
          }),
        },
      ),
    onSuccess: (data) => {
      const s = data.simulation;
      setSimulateOut(
        `Filter: ${s.filter_pass ? 'pass' : 'fail'} · ${s.steps.map((x) => x.preview).join(' → ')}`,
      );
    },
  });

  if (!isNew && detailQ.isLoading) return <LoadingState label="Automation laden…" />;

  const agents = agentsQ.data?.agents ?? [];

  return (
    <div className={styles.editorPanel}>
      <h2>{isNew ? 'Nieuwe workflow' : name}</h2>
      <div className={styles.tabRow}>
        <label>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /> Aan
        </label>
      </div>
      <Field label="Naam">
        <TextInput value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Beschrijving">
        <TextArea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <Field label="Trigger">
        <select
          className={leadStyles.filterInput}
          value={triggerType}
          onChange={(e) => setTriggerType(e.target.value as AutomationTriggerType)}
        >
          {AUTOMATION_TRIGGER_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </Field>
      {triggerType === 'event' ? (
        <Field label="Hub-event">
          <select
            className={leadStyles.filterInput}
            value={eventType}
            onChange={(e) => setEventType(e.target.value as AutomationHubEventType)}
          >
            {AUTOMATION_HUB_EVENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {WEBHOOK_EVENT_LABELS[t]}
              </option>
            ))}
          </select>
        </Field>
      ) : null}
      <Field label="Filter: campagne-ID (optioneel)">
        <TextInput value={campaignFilter} onChange={(e) => setCampaignFilter(e.target.value)} placeholder="UUID" />
      </Field>

      <AutomationStepListEditor steps={steps} agents={agents} onChange={setSteps} />

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '1rem' }}>
        <Button type="button" disabled={saveM.isPending} onClick={() => saveM.mutate()}>
          {saveM.isPending ? 'Opslaan…' : 'Opslaan'}
        </Button>
        <Button type="button" variant="secondary" disabled={simulateM.isPending} onClick={() => simulateM.mutate()}>
          Simulate
        </Button>
      </div>
      {simulateOut ? <p className={styles.outputPre}>{simulateOut}</p> : null}
      {saveM.isError && saveM.error instanceof ApiError ? <p>{saveM.error.message}</p> : null}

      {!isNew && automationId ? (
        <>
          <h3 style={{ marginTop: '1.5rem' }}>Recente runs</h3>
          {(runsQ.data?.runs ?? []).slice(0, 8).map((r) => (
            <div key={r.id} className={styles.runRow}>
              <span>
                <code>{r.status}</code> · {new Date(r.created_at).toLocaleString('nl-NL')}
              </span>
              <span style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                {r.trace_id ? <TraceLink traceId={r.trace_id} /> : null}
                <Button type="button" variant="ghost" onClick={() => onRunSelect(r.id)}>
                  Details
                </Button>
              </span>
            </div>
          ))}
        </>
      ) : null}
    </div>
  );
}
